/**
 * First-touch attribution → Cart Attributes
 *
 * Records where a visitor FIRST came from (utm_* params, or the referring site)
 * and writes it onto the Shopify cart so it survives through to the order.
 * Needed because checkout runs on Shiprocket Fastrr (off-domain), so the
 * storefront can't tag the order directly.
 *
 * First touch wins: once recorded it is never overwritten and never expires.
 * Read the result in Shopify admin → Order → "Additional details".
 */
(function () {
  'use strict';

  // Cart attributes prefixed with "_" are visible in the Shopify admin but are
  // hidden from the customer's order confirmation email and order status page.
  // Drop this to '' if you'd rather have them shown to the customer too.
  var ATTR_PREFIX = '_';

  var STORAGE_KEY = 'anveshan_attribution';
  var SYNCED_KEY = 'anveshan_attribution_synced';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  // utm_source set by the PDP share button (see snippets/share.liquid).
  var SHARE_SOURCE = 'product_share';

  // Grabbed before anything else can wrap it. Our own cart writes go through
  // this directly, so they stay invisible to other cart interceptors on the
  // site (e.g. the coins widget) — we only touch attributes, never line items,
  // so nothing else has any reason to re-render.
  var nativeFetch = window.fetch;

  /* ---------------------------------------------------------------- storage */

  function readStore() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function writeStore(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      /* private mode / storage full — attribution degrades to this pageview only */
    }
  }

  /* ------------------------------------------------------------- capturing */

  function currentUtms() {
    var params = new URLSearchParams(window.location.search);
    var found = {};
    var hasAny = false;

    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        found[key] = value.slice(0, 255);
        hasAny = true;
      }
    });

    return hasAny ? found : null;
  }

  // An external referrer with no UTMs still counts as a first touch, so organic
  // shares (someone pasting a bare link) aren't silently logged as direct.
  function referrerSource() {
    var ref = document.referrer;
    if (!ref) return null;
    try {
      var host = new URL(ref).hostname;
      if (host === window.location.hostname) return null;
      return { utm_source: host, utm_medium: 'referral' };
    } catch (err) {
      return null;
    }
  }

  function capture() {
    var store = readStore();
    var incoming = currentUtms() || (store ? null : referrerSource());

    if (!store && !incoming) return null;

    var changed = false;

    // First touch is written once and never overwritten.
    if (!store) {
      store = {
        touch: incoming,
        captured_at: Date.now(),
        landing_page: window.location.pathname + window.location.search,
        referrer: document.referrer || 'direct'
      };
      changed = true;
    }

    // A share visit is always recorded, even when first touch is already
    // locked to something else — otherwise repeat customers arriving from a
    // shared link would never be counted. Holds the most recent share visit.
    if (incoming && incoming.utm_source === SHARE_SOURCE) {
      store.share = {
        product: incoming.utm_content || '',
        campaign: incoming.utm_campaign || '',
        at: new Date().toISOString()
      };
      changed = true;
    }

    if (changed) writeStore(store);
    return store;
  }

  /* ------------------------------------------------------- cart attributes */

  function buildAttributes(store) {
    if (!store) return null;

    var attrs = {};
    var touch = store.touch || {};

    UTM_KEYS.forEach(function (key) {
      attrs[ATTR_PREFIX + key] = touch[key] || '';
    });

    attrs[ATTR_PREFIX + 'landing_page'] = store.landing_page || '';
    attrs[ATTR_PREFIX + 'referrer'] = store.referrer || 'direct';

    // Flat yes/no column so share orders are filterable without parsing UTMs.
    // Independent of first touch — set whenever a share visit has happened.
    var share = store.share;
    attrs[ATTR_PREFIX + 'shared_via'] = share ? 'pdp_share_button' : '';
    attrs[ATTR_PREFIX + 'share_product'] = share ? share.product : '';
    attrs[ATTR_PREFIX + 'share_seen_at'] = share ? share.at : '';

    return attrs;
  }

  function syncToCart(store, force) {
    var attrs = buildAttributes(store);
    if (!attrs) return;

    var fingerprint = JSON.stringify(attrs);

    // Don't re-POST identical attributes on every pageview.
    try {
      if (!force && sessionStorage.getItem(SYNCED_KEY) === fingerprint) return;
    } catch (err) {
      /* sessionStorage unavailable — fall through and just write */
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    nativeFetch.call(window, root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/update failed: ' + res.status);
        try {
          sessionStorage.setItem(SYNCED_KEY, fingerprint);
        } catch (err) {
          /* ignore */
        }
      })
      .catch(function (err) {
        console.warn('[attribution] could not write cart attributes:', err);
      });
  }

  /* ------------------------------------------------------------------ init */

  var attribution = capture();

  // Expose for debugging and for other scripts that want the first touch.
  window.AnveshanAttribution = {
    get: function () {
      return readStore();
    },
    attributes: function () {
      return buildAttributes(readStore());
    },
    sync: function () {
      syncToCart(readStore(), true);
    },
    reset: function () {
      try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(SYNCED_KEY);
      } catch (err) {
        /* ignore */
      }
    }
  };

  if (attribution) syncToCart(attribution, false);

  // Shopify clears cart attributes when the cart empties, and the Fastrr
  // checkout reads them at open time — so re-assert after every add to cart.
  window.fetch = function () {
    var url = arguments[0];
    var href = typeof url === 'string' ? url : url && url.url ? url.url : '';

    // Always call through bound to window, and never let anything in here
    // change what the caller receives — this wrapper sits in front of every
    // fetch on the site, including cart and checkout.
    var result = nativeFetch.apply(window, arguments);

    if (href && href.indexOf('/cart/add') !== -1 && result && typeof result.then === 'function') {
      result
        .then(function () {
          syncToCart(readStore(), true);
        })
        .catch(function () {
          /* the add itself failed; nothing to attribute */
        });
    }

    return result;
  };
})();
